import { useEffect, useRef, useState, type RefObject } from "react";
import type { MapMouseEvent, MapRef } from "react-map-gl/mapbox";

import {
  CELL_DRAG_THRESHOLD_PX,
  cellsInBboxSelection,
  createPendingCellDrag,
  hoverCursorForMeshPointer,
  resolveMeshPointerTarget,
  type PendingCellDrag,
  type PendingEdgeDrag,
} from "components/PlotMap/utils/meshPointerUtils";
import type { BoundaryPoint } from "context/PlotMap/geo";
import {
  captureEdgeSlideConstraint,
  captureVertexSlideConstraint,
  findContinuousEdgeChain,
} from "context/PlotMap/gridMeshSlide";
import { rotationCellsForCornerVertex } from "context/PlotMap/gridMeshTopology";
import {
  cellGroupCenter,
  cellRingVertexOrigins,
} from "context/PlotMap/gridMeshTransform";
import {
  normalizeDegreesDelta,
  pointerAngleFromNorthDegrees,
} from "context/PlotMap/gridUtils";
import type {
  FrozenEdgeSlideConstraint,
  FrozenVertexSlideConstraint,
  GridEdgeRef,
  GridEdgeSlideResult,
  GridMesh,
} from "context/PlotMap/gridMeshTypes";

export type MeshDragTarget =
  | {
      chainVertexIds: string[];
      edge: GridEdgeRef;
      frozenConstraint: FrozenEdgeSlideConstraint | null;
      lastLatitude: number;
      lastLongitude: number;
      mode: "move" | "slide";
      type: "mesh-edge";
    }
  | {
      cellIds: string[];
      lastLatitude: number;
      lastLongitude: number;
      type: "mesh-cells";
    }
  | {
      cellIds: string[];
      center: BoundaryPoint;
      didDetach: boolean;
      origins: Record<string, BoundaryPoint>;
      startPointerAngleDegrees: number;
      type: "mesh-rotate";
    }
  | {
      frozenConstraint: FrozenVertexSlideConstraint | null;
      mode: "move" | "slide";
      type: "mesh-vertex";
      vertexId: string;
    }
  | {
      startLatitude: number;
      startLongitude: number;
      type: "mesh-bbox";
    };

interface UseMeshEditInteractionParams {
  beginGridHistoryGesture: () => void;
  canEditMesh: boolean;
  clearGridSelection: () => void;
  /** Wired from context; plot grid right-clicks do not call this (see handleContextMenu). */
  deleteGridCell: (cellId: string) => void;
  /** Wired from context; plot grid right-clicks do not call this (see handleContextMenu). */
  deleteGridEdge: (edge: GridEdgeRef) => void;
  /** Wired from context; plot grid right-clicks do not call this (see handleContextMenu). */
  deleteGridVertex: (vertexId: string) => void;
  detachAndTranslateSelection: (
    cellIds: string[],
    delta: { latitude: number; longitude: number },
  ) => void;
  endGridHistoryGesture: () => void;
  finalizeGridEdgeSlide: (edge: GridEdgeRef, chainVertexIds?: string[]) => void;
  /** Wired from context; plot grid map clicks do not call this (see handleMapClick). */
  insertGridVertexOnEdge: (edge: GridEdgeRef, point: BoundaryPoint) => void;
  isRotateModifierHeld: (event: MapMouseEvent) => boolean;
  isShiftHeld: (event: MapMouseEvent) => boolean;
  mapRef: RefObject<MapRef | null>;
  mesh: GridMesh | null | undefined;
  moveGridEdge: (
    edge: GridEdgeRef,
    delta: { latitude: number; longitude: number },
    chainVertexIds?: string[],
  ) => void;
  moveGridVertex: (vertexId: string, point: BoundaryPoint) => void;
  rotateContiguousCells: (
    cellIds: string[],
    center: BoundaryPoint,
    degrees: number,
    origins: Record<string, BoundaryPoint>,
    detachShared: boolean,
  ) => Record<string, BoundaryPoint>;
  selectedCellIds: string[];
  setSelectedCellIds: (cellIds: string[]) => void;
  setSelectedVertexId: (vertexId: string | null) => void;
  slideGridEdge: (
    edge: GridEdgeRef,
    pointer: BoundaryPoint,
    chainVertexIds?: string[],
    frozenConstraint?: FrozenEdgeSlideConstraint | null,
  ) => GridEdgeSlideResult;
  slideGridVertex: (
    vertexId: string,
    pointer: BoundaryPoint,
    frozenConstraint?: FrozenVertexSlideConstraint | null,
  ) => void;
  toggleCellSelection: (cellId: string) => void;
}

interface UseMeshEditInteractionResult {
  bboxGeoJson: {
    type: "FeatureCollection";
    features: Array<{
      type: "Feature";
      geometry: {
        type: "LineString";
        coordinates: Array<[number, number]>;
      };
      properties: Record<string, never>;
    }>;
  } | null;
  handleContextMenu: (event: MapMouseEvent) => boolean;
  handleMapClick: (event: MapMouseEvent) => boolean;
  handleMouseDown: (event: MapMouseEvent) => boolean;
  handleMouseMove: (event: MapMouseEvent) => boolean;
  handleMouseUp: () => void;
  isDragging: boolean;
  resolveHoverCursor: (
    event: MapMouseEvent,
    rotateModifierHeld?: boolean,
  ) => string | undefined;
}

const isGeometryHistoryDrag = (
  target: MeshDragTarget,
): target is Exclude<MeshDragTarget, { type: "mesh-bbox" }> =>
  target.type === "mesh-vertex" ||
  target.type === "mesh-edge" ||
  target.type === "mesh-cells" ||
  target.type === "mesh-rotate";

const gestureLabel = (
  target: Exclude<MeshDragTarget, { type: "mesh-bbox" }>,
): string =>
  target.type === "mesh-edge" ? `${target.type}:${target.mode}` : target.type;

const useMeshEditInteraction = ({
  beginGridHistoryGesture,
  canEditMesh,
  clearGridSelection,
  deleteGridCell: _deleteGridCell,
  deleteGridEdge: _deleteGridEdge,
  deleteGridVertex: _deleteGridVertex,
  detachAndTranslateSelection,
  endGridHistoryGesture,
  finalizeGridEdgeSlide,
  insertGridVertexOnEdge: _insertGridVertexOnEdge,
  isRotateModifierHeld,
  isShiftHeld,
  mapRef,
  mesh,
  moveGridEdge,
  moveGridVertex,
  rotateContiguousCells,
  selectedCellIds,
  setSelectedCellIds,
  setSelectedVertexId,
  slideGridEdge,
  slideGridVertex,
  toggleCellSelection,
}: UseMeshEditInteractionParams): UseMeshEditInteractionResult => {
  // Ref + state for the same drag target serve different paths:
  // - Ref: pointer handlers read/write synchronously (including mid-drag field
  //   updates) without waiting on a render or closing over a stale target.
  // - State: re-render for isDragging and effects/UI that depend on drag status
  //   (e.g. window mouseup, overlays).
  const draggingTargetRef = useRef<MeshDragTarget | null>(null);
  const [draggingTarget, setDraggingTarget] = useState<MeshDragTarget | null>(
    null,
  );
  // Same ref + state split as draggingTarget (sync finalize vs bbox overlay render).
  const [bboxCorner, setBboxCorner] = useState<BoundaryPoint | null>(null);
  const bboxCornerRef = useRef<BoundaryPoint | null>(null);
  const cellSelectionHandledCellIdRef = useRef<string | null>(null);
  const pendingCellDragRef = useRef<PendingCellDrag | null>(null);
  const pendingEdgeDragRef = useRef<PendingEdgeDrag | null>(null);

  const setBboxCornerBoth = (corner: BoundaryPoint | null) => {
    bboxCornerRef.current = corner;
    setBboxCorner(corner);
  };

  const beginDrag = (target: MeshDragTarget, event: MapMouseEvent) => {
    event.originalEvent.preventDefault();
    if (isGeometryHistoryDrag(target)) {
      console.info(
        "[useMeshEditInteraction] begin gesture",
        JSON.stringify({ type: gestureLabel(target) }),
      );
      beginGridHistoryGesture();
    }
    draggingTargetRef.current = target;
    setDraggingTarget(target);
    mapRef.current?.getMap().dragPan.disable();
  };

  const startPendingCellDrag = (
    cellIds: string[],
    pointer: BoundaryPoint,
    event: MapMouseEvent,
  ) => {
    pendingCellDragRef.current = createPendingCellDrag(
      cellIds,
      pointer,
      event.point,
    );
    event.originalEvent.preventDefault();
    mapRef.current?.getMap().dragPan.disable();
  };

  const finalizeMeshBboxSelection = (
    start: BoundaryPoint,
    end: BoundaryPoint,
  ) => {
    if (mesh == null) {
      return;
    }

    setSelectedCellIds(cellsInBboxSelection(mesh, start, end));
  };

  const endPointerInteraction = () => {
    const hadPendingCellDrag = pendingCellDragRef.current != null;
    const hadPendingEdgeDrag = pendingEdgeDragRef.current != null;
    pendingCellDragRef.current = null;
    pendingEdgeDragRef.current = null;

    const target = draggingTargetRef.current;
    if (target == null) {
      if (hadPendingCellDrag || hadPendingEdgeDrag) {
        mapRef.current?.getMap().dragPan.enable();
      }
      // Leave cellSelectionHandledCellIdRef set so the deferred Map click can
      // suppress a duplicate toggle; click clears it when consumed.
      return;
    }

    if (target.type === "mesh-bbox" && bboxCornerRef.current != null) {
      finalizeMeshBboxSelection(
        {
          latitude: target.startLatitude,
          longitude: target.startLongitude,
        },
        bboxCornerRef.current,
      );
    }

    if (isGeometryHistoryDrag(target)) {
      if (target.type === "mesh-edge" && target.mode === "slide") {
        finalizeGridEdgeSlide(target.edge, target.chainVertexIds);
      }
      console.info(
        "[useMeshEditInteraction] end gesture",
        JSON.stringify({ type: gestureLabel(target) }),
      );
      endGridHistoryGesture();
    }

    draggingTargetRef.current = null;
    setDraggingTarget(null);
    setBboxCornerBoth(null);
    mapRef.current?.getMap().dragPan.enable();
  };
  // Prefer a ref over useCallback in the window mouseup effect deps.
  // Example: Shift+drag bbox — mousedown starts mesh-bbox → effect attaches window
  // mouseup; mousemove updates bboxCorner and re-renders, recreating
  // endPointerInteraction (it closes over mesh/setSelectedCellIds). With useCallback in
  // the effect deps, each identity change would remove/re-add the listener
  // mid-drag. The ref keeps one subscription for the drag (deps: draggingTarget)
  // while mouseup still calls the latest finalize logic.
  const endPointerInteractionRef = useRef(endPointerInteraction);
  endPointerInteractionRef.current = endPointerInteraction;

  useEffect(() => {
    const handleWindowMouseUp = () => {
      if (draggingTargetRef.current != null) {
        return;
      }
      const hadPending =
        pendingCellDragRef.current != null ||
        pendingEdgeDragRef.current != null;
      if (!hadPending) {
        return;
      }
      pendingCellDragRef.current = null;
      pendingEdgeDragRef.current = null;
      mapRef.current?.getMap().dragPan.enable();
    };

    window.addEventListener("mouseup", handleWindowMouseUp);
    return () => {
      window.removeEventListener("mouseup", handleWindowMouseUp);
    };
  }, [mapRef]);

  useEffect(() => {
    if (draggingTarget == null) {
      return;
    }

    const handleWindowMouseUp = () => {
      endPointerInteractionRef.current();
    };

    window.addEventListener("mouseup", handleWindowMouseUp);
    return () => {
      window.removeEventListener("mouseup", handleWindowMouseUp);
    };
  }, [draggingTarget]);

  const resolveHoverCursor = (
    event: MapMouseEvent,
    rotateModifierHeld?: boolean,
  ): string | undefined => {
    if (
      !canEditMesh ||
      mesh == null ||
      mapRef.current == null ||
      draggingTargetRef.current != null
    ) {
      return undefined;
    }

    const shiftHeld = isShiftHeld(event);
    // Keyboard updates pass the live modifier. A stored mousemove still has
    // the modifiers from when it fired, so it cannot be trusted on its own.
    const rotateHeld = rotateModifierHeld ?? isRotateModifierHeld(event);
    const pointer: BoundaryPoint = {
      latitude: event.lngLat.lat,
      longitude: event.lngLat.lng,
    };
    const target = resolveMeshPointerTarget(
      mapRef.current,
      mesh,
      event.point,
      pointer,
      { shiftHeld },
    );

    return hoverCursorForMeshPointer(
      mapRef.current,
      mesh,
      target,
      shiftHeld,
      rotateHeld,
    );
  };

  const handleContextMenu = (event: MapMouseEvent): boolean => {
    if (
      !canEditMesh ||
      mesh == null ||
      mapRef.current == null ||
      draggingTargetRef.current != null
    ) {
      return false;
    }

    const pointer: BoundaryPoint = {
      latitude: event.lngLat.lat,
      longitude: event.lngLat.lng,
    };
    const target = resolveMeshPointerTarget(
      mapRef.current,
      mesh,
      event.point,
      pointer,
      { shiftHeld: isShiftHeld(event) },
    );

    if (
      target.type === "vertex" ||
      target.type === "edge" ||
      target.type === "cell"
    ) {
      // Right-click no longer deletes plot-grid cells, edges, or vertices.
      // deleteGridCell, deleteGridEdge, and deleteGridVertex remain for a
      // possible future restore. Boundary editing still deletes on right-click.
      event.originalEvent.preventDefault();
      return true;
    }

    return false;
  };

  const handleMapClick = (event: MapMouseEvent): boolean => {
    if (!canEditMesh || mesh == null || draggingTargetRef.current != null) {
      return false;
    }

    const clickPoint: BoundaryPoint = {
      latitude: event.lngLat.lat,
      longitude: event.lngLat.lng,
    };

    if (mapRef.current != null) {
      const target = resolveMeshPointerTarget(
        mapRef.current,
        mesh,
        event.point,
        clickPoint,
        { shiftHeld: isShiftHeld(event) },
      );

      if (target.type === "vertex") {
        return true;
      }

      if (target.type === "edge") {
        // Unlike grower/site boundaries, plot grids do not insert a vertex on edge
        // click. insertGridVertexOnEdge remains for a possible future restore.
        return true;
      }

      if (target.type === "cell") {
        if (cellSelectionHandledCellIdRef.current === target.cellId) {
          cellSelectionHandledCellIdRef.current = null;
          return true;
        }
        if (isShiftHeld(event)) {
          toggleCellSelection(target.cellId);
        } else {
          setSelectedCellIds([target.cellId]);
        }
        return true;
      }
    }

    if (cellSelectionHandledCellIdRef.current != null) {
      cellSelectionHandledCellIdRef.current = null;
    }
    if (!event.originalEvent.shiftKey) {
      clearGridSelection();
    }
    return true;
  };

  const handleMouseDown = (event: MapMouseEvent): boolean => {
    if (
      !canEditMesh ||
      mesh == null ||
      mapRef.current == null ||
      event.originalEvent.button !== 0
    ) {
      return false;
    }

    const pointer: BoundaryPoint = {
      latitude: event.lngLat.lat,
      longitude: event.lngLat.lng,
    };
    const shiftHeld = isShiftHeld(event);
    const target = resolveMeshPointerTarget(
      mapRef.current,
      mesh,
      event.point,
      pointer,
      { shiftHeld },
    );

    if (target.type === "vertex") {
      if (isRotateModifierHeld(event)) {
        const cellIds = rotationCellsForCornerVertex(mesh, target.vertexId);
        const origins =
          cellIds == null ? null : cellRingVertexOrigins(mesh, cellIds);
        const center = origins == null ? null : cellGroupCenter(origins);
        if (cellIds != null && origins != null && center != null) {
          setSelectedVertexId(target.vertexId);
          beginDrag(
            {
              cellIds,
              center,
              didDetach: false,
              origins,
              startPointerAngleDegrees: pointerAngleFromNorthDegrees(
                center,
                pointer,
              ),
              type: "mesh-rotate",
            },
            event,
          );
          return true;
        }
      }

      const mode = shiftHeld ? "move" : "slide";
      const frozenConstraint =
        mode === "slide"
          ? captureVertexSlideConstraint(mesh, target.vertexId)
          : null;
      if (mode === "slide" && frozenConstraint == null) {
        return true;
      }
      setSelectedVertexId(target.vertexId);
      beginDrag(
        {
          frozenConstraint,
          mode,
          type: "mesh-vertex",
          vertexId: target.vertexId,
        },
        event,
      );
      return true;
    }

    if (target.type === "cell" && target.preferOverEdge) {
      const nextSelection = selectedCellIds.includes(target.cellId)
        ? selectedCellIds.filter((id) => id !== target.cellId)
        : [...selectedCellIds, target.cellId];
      toggleCellSelection(target.cellId);
      cellSelectionHandledCellIdRef.current = target.cellId;
      startPendingCellDrag(
        nextSelection.length > 0 ? nextSelection : [target.cellId],
        pointer,
        event,
      );
      return true;
    }

    if (target.type === "edge") {
      const chainVertexIds = findContinuousEdgeChain(
        mesh,
        selectedCellIds,
        target.edge,
      );
      const mode = shiftHeld ? "move" : "slide";
      const frozenConstraint =
        mode === "slide" ? captureEdgeSlideConstraint(mesh, target.edge) : null;
      if (mode === "slide" && frozenConstraint == null) {
        return true;
      }
      pendingEdgeDragRef.current = {
        chainVertexIds,
        edge: target.edge,
        frozenConstraint,
        lastLatitude: pointer.latitude,
        lastLongitude: pointer.longitude,
        mode,
        startX: event.point.x,
        startY: event.point.y,
      };
      event.originalEvent.preventDefault();
      mapRef.current?.getMap().dragPan.disable();
      return true;
    }

    if (target.type === "cell") {
      let nextSelection = selectedCellIds;
      if (!selectedCellIds.includes(target.cellId)) {
        setSelectedCellIds([target.cellId]);
        nextSelection = [target.cellId];
      }

      cellSelectionHandledCellIdRef.current = target.cellId;
      startPendingCellDrag(
        nextSelection.length > 0 ? nextSelection : [target.cellId],
        pointer,
        event,
      );
      return true;
    }

    if (shiftHeld) {
      beginDrag(
        {
          startLatitude: pointer.latitude,
          startLongitude: pointer.longitude,
          type: "mesh-bbox",
        },
        event,
      );
      setBboxCornerBoth(pointer);
      return true;
    }

    return false;
  };

  const handleMouseMove = (event: MapMouseEvent): boolean => {
    const pendingCellDrag = pendingCellDragRef.current;
    if (pendingCellDrag != null && draggingTargetRef.current == null) {
      const distancePx = Math.hypot(
        event.point.x - pendingCellDrag.startX,
        event.point.y - pendingCellDrag.startY,
      );
      if (distancePx >= CELL_DRAG_THRESHOLD_PX) {
        pendingCellDragRef.current = null;
        beginDrag(
          {
            cellIds: pendingCellDrag.cellIds,
            lastLatitude: pendingCellDrag.lastLatitude,
            lastLongitude: pendingCellDrag.lastLongitude,
            type: "mesh-cells",
          },
          event,
        );
      }
    }

    const pendingEdgeDrag = pendingEdgeDragRef.current;
    if (pendingEdgeDrag != null && draggingTargetRef.current == null) {
      const distancePx = Math.hypot(
        event.point.x - pendingEdgeDrag.startX,
        event.point.y - pendingEdgeDrag.startY,
      );
      if (distancePx >= CELL_DRAG_THRESHOLD_PX) {
        pendingEdgeDragRef.current = null;
        beginDrag(
          {
            chainVertexIds: pendingEdgeDrag.chainVertexIds,
            edge: pendingEdgeDrag.edge,
            frozenConstraint: pendingEdgeDrag.frozenConstraint,
            lastLatitude: pendingEdgeDrag.lastLatitude,
            lastLongitude: pendingEdgeDrag.lastLongitude,
            mode: pendingEdgeDrag.mode,
            type: "mesh-edge",
          },
          event,
        );
      }
    }

    const target = draggingTargetRef.current;
    if (target == null) {
      return false;
    }

    const pointer: BoundaryPoint = {
      latitude: event.lngLat.lat,
      longitude: event.lngLat.lng,
    };

    if (target.type === "mesh-vertex") {
      if (target.mode === "slide") {
        slideGridVertex(target.vertexId, pointer, target.frozenConstraint);
      } else {
        moveGridVertex(target.vertexId, pointer);
      }
      return true;
    }

    if (target.type === "mesh-edge") {
      if (target.mode === "slide") {
        const next = slideGridEdge(
          target.edge,
          pointer,
          target.chainVertexIds,
          target.frozenConstraint,
        );
        draggingTargetRef.current = {
          ...target,
          chainVertexIds: next.chainVertexIds,
          edge: next.edge,
        };
      } else {
        const delta = {
          latitude: pointer.latitude - target.lastLatitude,
          longitude: pointer.longitude - target.lastLongitude,
        };
        moveGridEdge(target.edge, delta, target.chainVertexIds);
        draggingTargetRef.current = {
          ...target,
          lastLatitude: pointer.latitude,
          lastLongitude: pointer.longitude,
        };
      }
      return true;
    }

    if (target.type === "mesh-rotate") {
      const pointerAngleDegrees = pointerAngleFromNorthDegrees(
        target.center,
        pointer,
      );
      const degrees = normalizeDegreesDelta(
        pointerAngleDegrees - target.startPointerAngleDegrees,
      );
      target.origins = rotateContiguousCells(
        target.cellIds,
        target.center,
        degrees,
        target.origins,
        !target.didDetach,
      );
      target.didDetach = true;
      return true;
    }

    if (target.type === "mesh-cells") {
      const delta = {
        latitude: pointer.latitude - target.lastLatitude,
        longitude: pointer.longitude - target.lastLongitude,
      };
      detachAndTranslateSelection(target.cellIds, delta);
      draggingTargetRef.current = {
        ...target,
        lastLatitude: pointer.latitude,
        lastLongitude: pointer.longitude,
      };
      return true;
    }

    if (target.type === "mesh-bbox") {
      setBboxCornerBoth(pointer);
      return true;
    }

    return false;
  };

  const bboxGeoJson: UseMeshEditInteractionResult["bboxGeoJson"] =
    draggingTarget?.type === "mesh-bbox" && bboxCorner != null
      ? {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              geometry: {
                type: "LineString",
                coordinates: [
                  [draggingTarget.startLongitude, draggingTarget.startLatitude],
                  [bboxCorner.longitude, draggingTarget.startLatitude],
                  [bboxCorner.longitude, bboxCorner.latitude],
                  [draggingTarget.startLongitude, bboxCorner.latitude],
                  [draggingTarget.startLongitude, draggingTarget.startLatitude],
                ],
              },
              properties: {},
            },
          ],
        }
      : null;

  return {
    bboxGeoJson,
    handleContextMenu,
    handleMapClick,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp: endPointerInteraction,
    isDragging: draggingTarget != null,
    resolveHoverCursor,
  };
};

export default useMeshEditInteraction;
